import { Chip, Divider, IconButton, Tooltip, Zoom } from "@mui/material";
import { DateTime } from "luxon";
import { Fragment, ReactElement, createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbActivityHeartbeat, TbArrowDown, TbHistory } from "react-icons/tb";

import { softColors } from "@/constants/colors";
import { passengerStatuses } from "@/constants/passengerStatuses";
import { PASSENGER_STATUS } from "@/constants/status";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import { usePlanActions } from "@/stores/use-plan-table";
import { IPassengerDataStatusMilestones, IPassengerDataStatusMilestonesItem } from "@/types/tasks.types";

function PassengerStatusMileStonesModal({
    children,
    statusMilestones,
    rowId,
}: {
    children: ReactElement;
    statusMilestones: IPassengerDataStatusMilestones;
    rowId: string;
}) {
    const { updateShowPassengerHistoryDialog } = usePlanActions();
    if (!statusMilestones) return children;

    return (
        <NoMaxWidthTooltip
            title={
                <PassengerStatusMileStonesModalContent statusMilestones={statusMilestones}>
                    <Tooltip title="Geçmişi Görüntüle">
                        <IconButton component="div" onClick={() => updateShowPassengerHistoryDialog(rowId)}>
                            <TbHistory />
                        </IconButton>
                    </Tooltip>
                </PassengerStatusMileStonesModalContent>
            }
            slots={{
                transition: Zoom,
            }}
            arrow
        >
            {children}
        </NoMaxWidthTooltip>
    );
}

function PassengerStatusMileStonesModalContent({
    statusMilestones,
    children,
}: {
    statusMilestones: IPassengerDataStatusMilestones;
    children: ReactElement;
}) {
    if (!statusMilestones) return <span>-</span>;
    const lastCompletedStatusIndex =
        ([...passengerStatuses].reverse().find((item) => {
            return statusMilestones[item.status] && statusMilestones[item.status]?.action_time;
        })?.index || 0) + 1;

    const statusEntries = Object.entries(statusMilestones);

    return (
        <div
            className=" text-sm py-2  flex flex-col   gap-1 "
            onDoubleClick={(e) => {
                e.stopPropagation();
            }}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 font-medium text-base ">
                    <TbActivityHeartbeat size={22} /> Yolcu Durum Takibi
                </div>
                {children}
            </div>
            <Divider className="!bg-gray-200   !w-[80%] !mx-auto" />
            <div className="px-3 pt-2">
                <ol className="relative  flex    flex-col gap-5 py-2   mx-2 ">
                    <div className=" absolute  -left-[1px] w-[2px] bg-gray-200 h-[calc(100%+10px)] -top-[5px]"></div>
                    <div
                        className=" absolute  -left-[1px] w-[2px]  -top-[5px]"
                        style={{
                            height: `calc(${(lastCompletedStatusIndex + 1) * 20}% + 10px)`,
                            backgroundColor: softColors.indigo.hover,
                        }}
                    ></div>

                    {statusEntries.map(([status, milestone], index) => (
                        <Fragment key={status}>
                            <li className="ms-6 flex gap-3 items-center">
                                <PassengerStatusMileStonesModalItem
                                    lastCompletedStatusIndex={lastCompletedStatusIndex}
                                    index={index}
                                    milestone={milestone}
                                    status={status}
                                />
                            </li>
                            {index !== Object.keys(statusMilestones).length - 1 && (
                                <PassengerStatusMileStoneDivider
                                    time={milestone}
                                    nextTime={statusEntries[index + 1] && statusEntries[index + 1][1]}
                                />
                            )}
                        </Fragment>
                    ))}
                </ol>
            </div>
        </div>
    );
}

function PassengerStatusMileStoneDivider({
    time,
    nextTime,
}: {
    time: IPassengerDataStatusMilestonesItem | null | undefined;
    nextTime: IPassengerDataStatusMilestonesItem | null | undefined;
}) {
    if (!time || !nextTime || !time.action_time || !nextTime.action_time) return null;
    const fromTime = DateTime.fromISO(time.action_time).setZone("Europe/Istanbul").set({ second: 0 });
    const toTime = DateTime.fromISO(nextTime.action_time).setZone("Europe/Istanbul").set({ second: 0 });
    const timeDiff = useMemo(() => {
        const diff = toTime.diff(fromTime, ["hours", "minutes"]);
        if (!diff.isValid) return null;
        return diff.hours > 0
            ? `${Math.floor(diff.hours)}h ${Math.round(diff.minutes)}m`
            : `${Math.round(diff.minutes)}m`;
    }, [fromTime, toTime]);

    return (
        <Chip
            className=" !w-fit shrink-0 grow-0 mx-auto bg-gray-300 font-medium"
            icon={createElement(TbArrowDown, { fontSize: 10, color: "gray" })}
            label={`Geçiş: ${timeDiff}`}
            size="small"
        />
    );
}
function PassengerStatusMileStonesModalItem({
    lastCompletedStatusIndex,
    index,
    milestone,
    status,
}: {
    milestone: IPassengerDataStatusMilestonesItem;
    status: string;
    index: number;
    lastCompletedStatusIndex: number;
}) {
    const { t } = useTranslation();
    if (!status) return <span>-</span>;
    const statusInfo = passengerStatuses.find((item) => item.status === status);
    if (!statusInfo) return <span>-</span>;
    const isCompleted = status === PASSENGER_STATUS.PENDING || lastCompletedStatusIndex - 1 >= index;

    return (
        <>
            <span
                style={{ backgroundColor: isCompleted ? softColors.indigo.bg : softColors.grey.bg }}
                className="absolute flex items-center justify-center w-8 h-8  shadow rounded-full -left-4 ring-1  ring-gray-300 "
            >
                {createElement(statusInfo.component(), {
                    size: 22,
                    color: isCompleted ? softColors.indigo.text : softColors.grey.text,
                })}
            </span>
            <div className="flex flex-col  w-72 min-h-10">
                <div className="font-semibold text-sm">{t("passengerStatuses." + statusInfo.status)}</div>
                <span className="text-gray-500 font-normal">
                    {t("passengerStatuses." + statusInfo.status + "_DESC")}
                </span>
            </div>

            <div className=" shrink-0 grow-0  ml-auto w-12">
                {(milestone && (
                    <>
                        <div className="text-gray-500 font-normal">
                            {DateTime.fromISO(milestone.action_time)
                                .setZone("Europe/Istanbul")
                                .setLocale("tr")
                                .toFormat("d LLL")}
                        </div>
                        <div className="text-gray-900 font-medium">
                            {DateTime.fromISO(milestone.action_time)
                                .setZone("Europe/Istanbul")
                                .setLocale("tr")
                                .toFormat("HH:mm")}
                        </div>
                    </>
                )) ||
                    (status === PASSENGER_STATUS.PENDING ? (
                        ""
                    ) : (
                        <div className="w-full h-full grid place-content-center justify-center">-</div>
                    ))}
            </div>
        </>
    );
}

export default PassengerStatusMileStonesModal;
